import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useGetAllAccountsQuery } from "../../../store/queries/account";
import {
  useCreateCustomCptVolumeMutation,
  useDeleteCustomCptVolumeMutation,
  useGetCustomCptVolumesQuery,
  useLazyGetCustomCptVolumeDownloadUrlQuery,
  useUpdateCustomCptVolumeMutation,
} from "../../../store/queries/customCptVolume";
import {
  CustomCptVolumeSpecialty,
  ICustomCptVolumeUploadListItem,
} from "../../../types/customCptVolume";
import {
  SpecialtyList,
  SpecialtyListDict,
} from "../../../types/Provider/Provider";

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${sizer(3)};
  gap: ${sizer(2)};
  flex-wrap: wrap;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${sizer(2)};
  align-items: flex-end;
  flex: 1;
  min-width: 280px;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(1)};
  min-width: 200px;

  label {
    font-weight: 600;
    font-size: 0.875rem;
    color: #495057;
  }
`;

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 1.5rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }

  .required {
    color: #e74c3c;
    margin-left: 0.25rem;
  }
`;

function extractApiErrorMessage(error: unknown): string {
  const e = error as {
    data?: { message?: string };
    error?: { data?: { message?: string } };
    message?: string;
  };
  if (e?.data?.message) return e.data.message;
  if (e?.error?.data?.message) return e.error.data.message;
  if (e?.message) return e.message;
  return "Request failed";
}

const SPECIALTY_OPTIONS: { label: string; value: CustomCptVolumeSpecialty }[] =
  Object.values(SpecialtyList).map((v) => ({
    label: SpecialtyListDict[v],
    value: v as CustomCptVolumeSpecialty,
  }));

export default function CustomCptVolumeTable() {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const [filterAccountId, setFilterAccountId] = useState<string | null>(null);
  const [filterSpecialty, setFilterSpecialty] =
    useState<CustomCptVolumeSpecialty | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [replaceScope, setReplaceScope] = useState<{
    accountId: string;
    specialty: CustomCptVolumeSpecialty;
  } | null>(null);

  const [uploadAccountId, setUploadAccountId] = useState("");
  const [uploadSpecialty, setUploadSpecialty] =
    useState<CustomCptVolumeSpecialty | null>(null);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const { data: accountsRes } = useGetAllAccountsQuery();
  const accountOptions = useMemo(() => {
    const rows = accountsRes?.data ?? [];
    return rows.map((a) => ({ label: a.name, value: a.id }));
  }, [accountsRes]);

  const {
    data: listRes,
    isLoading,
    refetch,
  } = useGetCustomCptVolumesQuery({
    accountId: filterAccountId ?? undefined,
    specialty: filterSpecialty ?? undefined,
  });

  const [createUpload, { isLoading: isCreating }] =
    useCreateCustomCptVolumeMutation();
  const [updateUpload] = useUpdateCustomCptVolumeMutation();
  const [deleteUpload] = useDeleteCustomCptVolumeMutation();
  const [fetchDownloadUrl] = useLazyGetCustomCptVolumeDownloadUrlQuery();

  const rows = listRes?.data ?? [];

  const openUploadDialog = (scope?: {
    accountId: string;
    specialty: CustomCptVolumeSpecialty;
  }) => {
    setReplaceScope(scope ?? null);
    setUploadAccountId(scope?.accountId ?? "");
    setUploadSpecialty(scope?.specialty ?? null);
    setUploadLabel("");
    setUploadFile(null);
    fileUploadRef.current?.clear();
    setUploadOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setReplaceScope(null);
  };

  const handleUpload = async () => {
    if (!uploadAccountId || !uploadSpecialty || !uploadLabel.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Missing fields",
        detail: "Account, specialty, and label are required.",
      });
      return;
    }
    if (!uploadFile) {
      toast.current?.show({
        severity: "error",
        summary: "No file",
        detail: "Please choose a CSV or Excel file.",
      });
      return;
    }
    try {
      const res = await createUpload({
        file: uploadFile,
        accountId: uploadAccountId,
        specialty: uploadSpecialty,
        label: uploadLabel.trim(),
      }).unwrap();
      const d = res.data;
      toast.current?.show({
        severity: "success",
        summary: "Upload saved",
        detail: `Parsed ${d.totalCpts} CPT codes, total volume ${d.totalVolume}.`,
      });
      closeUploadDialog();
      refetch();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Upload failed",
        detail: extractApiErrorMessage(err),
        life: 8000,
      });
    }
  };

  const handleDownload = async (row: ICustomCptVolumeUploadListItem) => {
    try {
      const res = await fetchDownloadUrl(row.id).unwrap();
      if (res.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Download failed",
        detail: extractApiErrorMessage(err),
      });
    }
  };

  const handleToggleActive = (row: ICustomCptVolumeUploadListItem) => {
    const next = !row.isActive;
    const run = async () => {
      try {
        await updateUpload({ id: row.id, isActive: next }).unwrap();
        toast.current?.show({
          severity: "success",
          summary: next ? "Activated" : "Deactivated",
          detail: next
            ? "This upload is now the active volume override for its account and specialty."
            : "Override deactivated for this upload.",
        });
        refetch();
      } catch (err) {
        toast.current?.show({
          severity: "error",
          summary: "Update failed",
          detail: extractApiErrorMessage(err),
        });
      }
    };
    if (!next) {
      confirmDialog({
        message:
          "Deactivate this upload? Smart Insights will fall back to default volume sources until another upload is active for this account and specialty.",
        header: "Deactivate override",
        icon: "pi pi-exclamation-triangle",
        accept: run,
      });
    } else {
      run().catch(() => {});
    }
  };

  const handleArchive = (row: ICustomCptVolumeUploadListItem) => {
    confirmDialog({
      message: `Archive "${row.label}"? Archived uploads no longer apply; Smart Insights uses default volume sources for that scope.`,
      header: "Archive upload",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteUpload(row.id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Archived",
            detail: "Upload archived.",
          });
          refetch();
        } catch (err) {
          toast.current?.show({
            severity: "error",
            summary: "Archive failed",
            detail: extractApiErrorMessage(err),
          });
        }
      },
    });
  };

  const specialtyBody = (row: ICustomCptVolumeUploadListItem) =>
    SpecialtyListDict[row.specialty as SpecialtyList] ?? row.specialty;

  const activeBody = (row: ICustomCptVolumeUploadListItem) =>
    row.isActive ? (
      <Tag severity="success" value="Active" />
    ) : (
      <Tag severity="info" value="Inactive" />
    );

  const createdBody = (row: ICustomCptVolumeUploadListItem) =>
    new Date(row.createdAt).toLocaleString();

  const actionsBody = (row: ICustomCptVolumeUploadListItem) => (
    <>
      <Button
        type="button"
        icon="pi pi-eye"
        className="p-button-rounded p-button-text p-button-sm"
        onClick={() => navigate(`/payer-rules/custom-cpt-volume/${row.id}`)}
        tooltip="View details"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        type="button"
        icon="pi pi-download"
        className="p-button-rounded p-button-text p-button-sm"
        onClick={() => {
          handleDownload(row).catch(() => {});
        }}
        tooltip="Download original file"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        type="button"
        icon={row.isActive ? "pi pi-times-circle" : "pi pi-check-circle"}
        className="p-button-rounded p-button-text p-button-sm"
        onClick={() => handleToggleActive(row)}
        tooltip={row.isActive ? "Deactivate" : "Set active"}
        tooltipOptions={{ position: "top" }}
      />
      <Button
        type="button"
        icon="pi pi-upload"
        className="p-button-rounded p-button-text p-button-sm"
        onClick={() =>
          openUploadDialog({
            accountId: row.accountId,
            specialty: row.specialty,
          })
        }
        tooltip="Replace (upload new file for this account & specialty)"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger p-button-sm"
        onClick={() => handleArchive(row)}
        tooltip="Archive"
        tooltipOptions={{ position: "top" }}
      />
    </>
  );

  return (
    <>
      <Toast ref={toast} />
      <Message
        severity="info"
        className="w-full mb-3"
        text="Only one upload can be active per account and provider specialty. While active, Smart Insights uses this file’s CPT volumes instead of EDI plus local order counts for Top CPT lists, contract management procedure volumes, and market price analysis. Deactivating restores the default behavior for that account and specialty."
      />
      <FlexWrapper>
        <FiltersRow>
          <FilterField>
            <label htmlFor="cptvol-filter-account">Account</label>
            <Dropdown
              inputId="cptvol-filter-account"
              value={filterAccountId}
              options={accountOptions}
              onChange={(e) => setFilterAccountId(e.value ?? null)}
              placeholder="All accounts"
              filter
              showClear
              className="w-full"
            />
          </FilterField>
          <FilterField>
            <label htmlFor="cptvol-filter-specialty">Specialty</label>
            <Dropdown
              inputId="cptvol-filter-specialty"
              value={filterSpecialty}
              options={SPECIALTY_OPTIONS}
              onChange={(e) => setFilterSpecialty(e.value ?? null)}
              placeholder="All specialties"
              showClear
              className="w-full"
            />
          </FilterField>
        </FiltersRow>
        <Button
          type="button"
          label="Upload"
          icon="pi pi-upload"
          className="p-button-raised"
          onClick={() => openUploadDialog()}
        />
      </FlexWrapper>

      <DataTable
        value={rows}
        loading={isLoading}
        dataKey="id"
        emptyMessage="No custom CPT volume uploads yet."
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50]}
      >
        <Column
          field="account.name"
          header="Account"
          body={(r: ICustomCptVolumeUploadListItem) => r.account?.name ?? "—"}
          sortable
        />
        <Column
          field="specialty"
          header="Specialty"
          body={specialtyBody}
          sortable
        />
        <Column field="label" header="Label" sortable />
        <Column field="isActive" header="Status" body={activeBody} sortable />
        <Column field="totalCpts" header="CPT count" sortable />
        <Column
          field="totalVolume"
          header="Total volume"
          sortable
          body={(r: ICustomCptVolumeUploadListItem) =>
            r.totalVolume.toLocaleString()
          }
        />
        <Column field="originalFileName" header="Original file" sortable />
        <Column
          field="createdAt"
          header="Created"
          body={createdBody}
          sortable
        />
        <Column
          header="Actions"
          body={actionsBody}
          style={{ width: "1rem", minWidth: "1rem" }}
          bodyStyle={{ whiteSpace: "nowrap" }}
        />
      </DataTable>

      <StyledDialog
        header={
          replaceScope
            ? "Replace custom CPT volume"
            : "Upload custom CPT volume"
        }
        visible={uploadOpen}
        style={{ width: "min(520px, 95vw)" }}
        onHide={closeUploadDialog}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text"
              onClick={closeUploadDialog}
            />
            <Button
              type="button"
              label={replaceScope ? "Replace" : "Upload"}
              icon="pi pi-check"
              loading={isCreating}
              onClick={() => {
                handleUpload().catch(() => {});
              }}
            />
          </div>
        }
      >
        <FormField>
          <label htmlFor="cptvol-account">
            Account <span className="required">*</span>
          </label>
          <Dropdown
            inputId="cptvol-account"
            value={uploadAccountId || null}
            options={accountOptions}
            onChange={(e) => setUploadAccountId(e.value ?? "")}
            placeholder="Select account"
            filter
            className="w-full"
            disabled={!!replaceScope}
          />
        </FormField>
        <FormField>
          <label htmlFor="cptvol-specialty">
            Provider specialty <span className="required">*</span>
          </label>
          <Dropdown
            inputId="cptvol-specialty"
            value={uploadSpecialty}
            options={SPECIALTY_OPTIONS}
            onChange={(e) => setUploadSpecialty(e.value)}
            placeholder="Select specialty"
            className="w-full"
            disabled={!!replaceScope}
          />
        </FormField>
        <FormField>
          <label htmlFor="cptvol-label">
            Label <span className="required">*</span>
          </label>
          <InputText
            id="cptvol-label"
            value={uploadLabel}
            onChange={(e) => setUploadLabel(e.target.value)}
            className="w-full"
            placeholder='e.g. "HMA United Jan–Oct 2025"'
          />
        </FormField>
        <FormField>
          <label>
            File <span className="required">*</span>
          </label>
          <small className="block text-600 mb-2">
            Accepted: CSV, Excel (.xlsx, .xls). Field name must be{" "}
            <code>file</code>.
          </small>
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            name="file"
            accept=".csv,.xlsx,.xls"
            maxFileSize={50_000_000}
            chooseLabel="Choose file"
            onSelect={(e) => {
              const f = e.files?.[0];
              setUploadFile(f ?? null);
            }}
            onClear={() => setUploadFile(null)}
          />
          {uploadFile && (
            <div className="text-sm text-700 mt-2">{uploadFile.name}</div>
          )}
        </FormField>
      </StyledDialog>
    </>
  );
}
